/**
 * Profile > Seed orders by email (D69; design rev 1 §1.1, critique R2 and R21): the one-line about, the address box,
 * Copy, New address (the garden's owner only), and on Free the Grow More sentence with Subscribe. No "Lately" (R2): what
 * an order did is said in the reply email and on the Seed vault's card.
 *
 * The address is made by the server the first time this is read by a gardener whose plan allows it (GET
 * /seed-orders/mine). A read that fails on our side says the standard sentence, reported (D31), never "try again".
 *
 * Where: UserProfile (?s=seeds). API: GET /seed-orders/mine (cached under SEED_ORDERS_MINE_KEY). Words (en/es):
 * profile.seeds.*, sitewide.loading.
 *
 * @module SeedOrdersScreen
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getSeedOrdersMine } from '@/shared/api/seedOrders';
import { ServerFailureNote, FailureText } from '@/shared/components/ServerFailure';
import { isServerFailure, rateLimitedText } from '@/shared/lib/serverFailure';
import { ProfileScreen } from '@/user/components/ProfileScreen';
import { SeedAddressCard } from './SeedAddressCard';
import { SEED_ORDERS_MINE_KEY } from './lib/seedOrderQueries';

/**
 * The screen.
 *
 * @param props.open - Whether it shows (UserProfile: ?s=seeds).
 * @param props.onClose - Back to the list.
 */
export const SeedOrdersScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery({ queryKey: SEED_ORDERS_MINE_KEY, queryFn: getSeedOrdersMine, enabled: open });

  return (
    <ProfileScreen open={open} title={t('profile.seeds.title')} onClose={onClose}>
      {!data && isError ? (
        isServerFailure(error)
          ? <ServerFailureNote error={error} where="SeedOrdersScreen" />
          : <p role="alert" className="text-sm text-destructive"><FailureText text={rateLimitedText(error) ?? t('profile.seeds.loadFailed')} /></p>
      ) : isLoading || !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      ) : (
        <SeedAddressCard mine={data} />
      )}
    </ProfileScreen>
  );
};
